import React, { useEffect, useRef, useState } from 'react';
import { Typography, Box, TextField, FormControl, FormLabel, FormGroup, FormControlLabel, Checkbox, Button, FormHelperText, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, CardContent, Card, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useForm, Controller, FieldError } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ApiCommunityClaimPlaceSubmit, ApiCommunityCreatePlaceSubmit, ApiCommunityGetPlaceCategory } from '@/api/community/verify-place';
import AutoCompletePlace from './AutoCompletePlace';
import Link from 'next/link';
import Image from 'next/image';
import { ICommunityClaimFormData, ICommunityClaimPlaceSubmitData, ICommunityCreatePlaceSubmitData } from '@/interfaces/community/verify-place';
import FromWithService from './ShowPlaceSelect';
import Swal from 'sweetalert2';
import { useRouter } from 'next/router';
import ReactDOMServer from 'react-dom/server';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import Grid from '@mui/material/Unstable_Grid2';

type Props = {
    valueType: number;
};

// สร้าง schema สำหรับการตรวจสอบ
const validationSchema = Yup.object().shape({
    contactName: Yup.string()
        .matches(/^[ก-๙a-zA-Z\s]+$/, 'กรุณากรอกเฉพาะตัวหนังสือเท่านั้น')
        .required('กรุณากรอกชื่อ นามสกุล'),
    contactPhone: Yup.string()
        .matches(/^[0-9]{10}$/, 'กรุณากรอกหมายเลขโทรศัพท์ให้ครบ 10 หลัก')
        .required('กรุณากรอกหมายเลขโทรศัพท์'),
    contactEmail: Yup.string()
        .email('กรุณากรอกอีเมลให้ถูกต้องตามรูปแบบ')
        .required('กรุณากรอกอีเมล'),
    commRegisNumber: Yup.string()
        .matches(/^[0-9]{13}$/, 'กรุณากรอกเลขทะเบียนพานิชให้ครบ 13 หลัก')
        .nullable(),
    companyName: Yup.string()
        .when('$valueType', {
            is: 1,  // ตรวจสอบค่า type ที่มาจาก context
            then: (schema) => schema
                .required('กรุณากรอกชื่อบริษัท / บริการ'),
            otherwise: (schema) => schema.notRequired(),
        }),
    checkboxType: Yup.array()
        .when('$valueType', {
            is: 1,  // ตรวจสอบค่า type ที่มาจาก context
            then: (schema) => schema
                .min(1, 'ต้องเลือกอย่างน้อย 1 หมวดหมู่') // ตรวจสอบว่าต้องเลือกอย่างน้อย 1 หมวดหมู่
                .required('ต้องเลือกอย่างน้อย 1 หมวดหมู่'), // ตรวจสอบว่าฟิลด์นี้บังคับกรอก
            otherwise: (schema) => schema.notRequired(),
        }),
    selectedPlace: Yup.mixed()
        .when('$valueType', {
            is: 0,
            then: (schema) => schema
                .required('กรุณากรอกชื่อบริษัท / บริการ'),
            otherwise: (schema) => schema.notRequired(),
        }),
    consentActualData: Yup.boolean()
        .oneOf([true], 'กรุณายอมรับเงื่อนไข')
        .required('กรุณายอมรับเงื่อนไข'),
});

const FormPlaceClaim = ({ valueType }: Props) => {

    const router = useRouter();
    const { query, replace } = router;

    const { control, formState: { errors }, register, handleSubmit, setValue, watch, reset } = useForm<ICommunityClaimFormData | any>({
        resolver: yupResolver(validationSchema),
        context: { valueType },
        defaultValues: {
            checkboxType: [],
            selectedPlace: null,
        },
    });

    const selectedPlaceValue = watch('selectedPlace');
    const [serviceTypes, setServiceTypes] = useState<{ label: string; value: number }[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState<any>(null);
  
    
  
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setValue('documentFile', reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const handleResponse = (response: any) => {
        if (response.status) {
            Swal.fire({
              
                text: response.message,
                icon: 'success',
                showCancelButton: true,
                confirmButtonText: 'หน้าหลัก',
                cancelButtonText: 'จัดการสถานที่'
            }).then((result) => {
                reset();
                const { placeId, ...restQuery } = query;
                replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
                if (result.isConfirmed) {
                    router.push('/');
                } else if (result.dismiss === Swal.DismissReason.cancel) {
                    router.push('/profile/place');
                }
            });
        } else {
            Swal.fire({
                title: 'Error!',
                text: response.message,
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    const onSubmit = async (data: ICommunityClaimFormData) => {
        setFormData(data);
        setOpenDialog(true);
    };


    const handleConfirm = async () => {
       
            if (valueType === 0) {
               
                const claimData = {
                    deviceType: "website",
                    placeId: String(formData.selectedPlace?.id || ""),
                    contactName: formData.contactName,
                    contactPhone: formData.contactPhone,
                    contactEmail: formData.contactEmail,
                    contactLineId: formData.contactLineId || "",
                    commRegisNumber: formData.commRegisNumber || "",
                    consentDataAnalytic: formData.consentDataAnalytic || false,
                    consentActualData: formData.consentActualData || false,
                    documentFile: formData.documentFile || "",
                };

                ApiCommunityClaimPlaceSubmit(claimData)
                    .then(response => {
                        handleResponse(response);
                    })
                    .catch(error => {
                        console.error("Error submitting claim:", error);
                        Swal.fire({
                            title: 'Error!',
                            text: 'An error occurred while submitting the claim.',
                            icon: 'error',
                            confirmButtonText: 'OK'
                        });
                    });

            } else if (valueType === 1) {
               
                
                const createData = {
                    deviceType: "website",
                    placeName: formData.companyName,
                    categoryIds: formData.checkboxType.join(","),
                    contactName: formData.contactName,
                    contactPhone: formData.contactPhone,
                    contactEmail: formData.contactEmail,
                    contactLineId: formData.contactLineId || "",
                    commRegisNumber: formData.commRegisNumber || "",
                    documentFile: formData.documentFile || "",
                    consentDataAnalytic: formData.consentDataAnalytic ? 1 : 2,
                    consentActualData: formData.consentActualData ? 1 : 2
                };
              
                // console.log(createData)
                // return

                ApiCommunityCreatePlaceSubmit(createData)
                    .then(response => {
                        handleResponse(response);
                    })
                    .catch(error => {
                        console.error("Error creating place:", error);
                        Swal.fire({
                            title: 'Error!',
                            text: 'An error occurred while creating the place.',
                            icon: 'error',
                            confirmButtonText: 'OK'
                        });
                    });
            }
        
        setOpenDialog(false); 
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await ApiCommunityGetPlaceCategory();
                setServiceTypes(res.data.map(({ categoryId, categoryTitle }: any) => ({
                    label: categoryTitle,
                    value: categoryId,
                })));
            } catch (error) {
                console.error("Error fetching categories", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // if (loading) return <Typography>Loading...</Typography>;

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                {valueType === 0 ? (
                    <>
                        {selectedPlaceValue && (
                            <Grid xs={12} sx={{ mt: 3 }}>
                                <FromWithService dataSet={selectedPlaceValue} />
                            </Grid>
                        )}
                        <Grid xs={12} sx={{ mt: 3 }}>
                            <FormControl fullWidth variant="outlined">
                                <FormLabel sx={{ mb: 1 }}>
                                    ค้นหาธุรกิจและแสดงสิทธิ์ธุรกิจของคุณ ที่มีอยู่ในระบบของ maaboom คุณสามารถค้นหาชื่อธุรกิจของคุณได้จากช่องค้นหาด้านล่าง หากไม่พบสามารถขอลงทะเบียนธุรกิจของคุณได้ที่ “ลงทะเบียนธุรกิจ(ใหม่)”
                                </FormLabel>
                            </FormControl>
                        </Grid>
                        <Grid xs={12}>
                            <AutoCompletePlace control={control} error={errors.selectedPlace as FieldError | undefined} setValue={setValue} />
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid xs={12} sx={{ mt: 3 }}>
                            <FormControl fullWidth variant="outlined">
                                <FormLabel sx={{ mb: 1 }}>
                                    สำหรับลงทะเบียนธุรกิจใหม่ กรุณาแจ้งรายละเอียดเบื้องต้นของธุรกิจคุณ
                                </FormLabel>
                            </FormControl>
                        </Grid>
                        <Grid xs={12}>
                            <Controller
                                name="companyName"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="ชื่อบริษัท / บริการ *"
                                        variant="outlined"
                                        fullWidth
                                        error={!!errors.companyName}
                                        helperText={errors.companyName?.message as string}
                                        sx={{ background: 'white' }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid xs={12}>
                            <FormControl component="fieldset" error={!!errors.checkboxType}>
                                <Typography variant="body1" component="small">
                                    ประเภทบริการ*
                                </Typography>
                                <FormGroup>
                                    <Grid container spacing={1} sx={{ mt: 1 }}>
                                        {serviceTypes.map(({ label, value }) => (
                                            <Grid xs={6} md={6} key={value}>
                                                <Controller
                                                    name="checkboxType"
                                                    control={control}
                                                    defaultValue={[]}
                                                    render={({ field }) => (
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    sx={{ p: 1 }}
                                                                    checked={field.value.includes(value)}
                                                                    onChange={(e) =>
                                                                        field.onChange(
                                                                            e.target.checked
                                                                                ? [...field.value, value]
                                                                                : field.value.filter((val: any) => val !== value)
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label={label}

                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </FormGroup>
                                {errors.checkboxType && <FormHelperText>{errors.checkboxType.message as string}</FormHelperText>}
                            </FormControl>

                        </Grid>
                    </>
                )}
                <Grid xs={12}>
                    <Box mt={2}>
                        <Typography variant="body1" component="small">
                            คุณสามารถ เพิ่ม, แก้ไข, ข้อมูล... หลังจากได้รับการตรวจสอบและอนุมัติจากเจ้าหน้าที่.... การอนุมัติจะส่ง “ยืนยัน” ไปที่ E-mail ของคุณ (ภายใน 1-7 วันทำการ)
                        </Typography>
                    </Box>
                    <Box sx={{ 
                        backgroundColor: '#f7f7f7',
                        my: 4, p: 4,
                        borderRadius: 2 
                    }}>
                        <Typography variant="h5" align="center" gutterBottom className="txt-sub-title">รายละเอียดผู้ติดต่อ</Typography>
                        <Grid container spacing={1} className="ctn-form">
                            <Grid xs={12}>
                                <Controller
                                    name="contactName"
                                    control={control}
                                    rules={{ required: 'กรุณากรอกชื่อ นามสกุล' }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="ชื่อ นามสกุล*"
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                            error={!!errors.contactName}
                                            helperText={errors.contactName?.message as string}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^[ก-๙a-zA-Z\s]*$/.test(value)) {
                                                    field.onChange(e); // อัปเดตค่าเฉพาะเมื่อเป็นตัวอักษรที่ถูกต้อง
                                                }
                                            }} // จับเฉพาะตัวหนังสือไทยและอังกฤษในฟิลด์นี้
                                            sx={{ background: 'white' }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid xs={12}>
                                <Controller
                                    name="contactPhone"
                                    control={control}
                                    rules={{ required: 'กรุณากรอกหมายเลขโทรศัพท์' }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="หมายเลขโทรศัพท์มือถือ*"
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                            error={!!errors.contactPhone}
                                            helperText={errors.contactPhone?.message as string}
                                            onChange={(e) => {
                                                let value = e.target.value.replace(/\D/g, ""); // กรองเฉพาะตัวเลข
                                                field.onChange(value); // อัปเดตค่าให้กับ react-hook-form (ถ้าใช้)
                                            }} // จับเฉพาะตัวเลขในฟิลด์นี้
                                            sx={{ background: 'white' }}
                                            inputProps={{ maxLength: 10 }}
                                        />
                                    )}
                                />

                            </Grid>
                            <Grid xs={12}>
                                <Controller
                                    name="contactEmail"
                                    control={control}
                                    rules={{ required: 'กรุณากรอกอีเมล' }} // ตรวจสอบว่าผู้ใช้ได้กรอกอีเมล
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type="email"
                                            label="อีเมล*"
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                            error={!!errors.contactEmail}
                                            helperText={errors.contactEmail?.message as string} // แสดงข้อความข้อผิดพลาดหากอีเมลไม่ถูกต้อง
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // รูปแบบของอีเมลที่ถูกต้องตามมาตรฐาน
                                                if (emailPattern.test(value)) {
                                                    field.onChange(e); // อัปเดตค่าเฉพาะเมื่อเป็นรูปแบบอีเมลที่ถูกต้อง
                                                }
                                            }}
                                            sx={{ background: 'white' }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid xs={12}>
                                <Controller
                                    name="contactLineId"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="LINE ID"
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                            sx={{ background: 'white' }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid xs={12}>
                                <Controller
                                    name="commRegisNumber"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="เลขทะเบียนพานิช"
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                            error={!!errors.commRegisNumber}
                                            helperText={errors.commRegisNumber?.message as string}
                                            sx={{ background: 'white' }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid xs={12}>
                                <TextField
                                    type="file"
                                    variant="outlined"
                                    fullWidth
                                    inputProps={{ style: { padding: '12px 14px' } }}
                                    sx={{ margin: '16px 0' }}
                                    onChange={handleFileChange}
                                    style={{ background: 'white' }}
                                />
                                <Typography variant="body2">
                                    <ul>
                                        <li>ไฟล์ที่รองรับ: .WEBP, .JPEG, .PNG ไม่เกิน 2MB.</li>
                                        <li>อัพโหลดเอกสาร เพื่อความรวดเร็วในการตรวจสอบข้อมูลลงทะเบียน...</li>
                                    </ul>
                                </Typography>
                            </Grid>
                            <Grid xs={12}>
                                <FormControlLabel
                                    control={<Checkbox {...register('consentActualData')} />}
                                    label={
                                        <Typography variant="body2">
                                            **ข้าพเจ้ายืนยันว่าข้อมูลและเอกสารข้างต้นทั้งหมดเป็นความจริง ถูกต้อง และครบถ้วนสมบูรณ์ (
                                            <Link href="https://maaboom.com/policy/terms" passHref legacyBehavior>
                                                <a target="_blank" rel="noopener noreferrer">อ่านเพิ่มเติม</a>
                                            </Link>
                                            ) กรุณากดยอมรับ
                                        </Typography>
                                    }
                                />
                                {errors.consentActualData && <FormHelperText error>{errors.consentActualData.message as string}</FormHelperText>}
                            </Grid>

                            <Grid xs={12}>
                                <FormControlLabel
                                    control={<Checkbox {...register('consentDataAnalytic')} />}
                                    label={
                                        <Typography variant="body2">
                                            ข้าพเจ้ายินยอมให้เก็บรวบรวมและใช้ข้อมูลส่วนบุคคลของข้าพเจ้า รวมถึงการวิจัยและวิเคราะห์ข้อมูลของข้าพเจ้า เพื่อประโยชน์สูงสุดในการพัฒนาเว็บไซต์ และบริการอื่นๆเพื่อให้ตอบสนองต่อความต้องการของข้าพเจ้ามากที่สุด (
                                            <Link href="https://maaboom.com/policy/privacy-policy" passHref legacyBehavior>
                                                <a target="_blank" rel="noopener noreferrer">อ่านเพิ่มเติม</a>
                                            </Link>
                                            )
                                        </Typography>
                                    }
                                />
                            </Grid>
                            <Grid xs={12}>
                                <Button
                                    sx={{ mt: 2, py: 1.5, fontSize: '16px' }}
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    fullWidth
                                >
                                    ลงทะเบียน
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>


            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
                maxWidth={"sm"}
            >
                <Box sx={{ m: 1 }}>
                    <DialogTitle id="alert-dialog-title" className="txt-bold txt-title" sx={{ px: 2, pt: 1 }}>
                        ยืนยันการบันทึกข้อมูล
                    </DialogTitle>
                    <DialogContent sx={{ px: 2, pb: 1 }}>
                        <DialogContentText id="alert-dialog-description">
                            <Grid container spacing={1} className="txt-default-responsive">
                                <Grid container xs={12}>
                                    <Grid xs={12} sm={6}>
                                        <Typography className="black">ชื่อบริษัท / บริการ:</Typography>
                                    </Grid>
                                    <Grid xs={12} sm={6}>
                                        <Typography>{formData?.selectedPlace?.placeName || formData?.companyName}</Typography>
                                    </Grid>
                                </Grid>
                                <Grid container xs={12}>
                                    <Grid xs={6}>
                                        <Typography className="black">ชื่อ นามสกุล:</Typography>
                                    </Grid>
                                    <Grid xs={6}>
                                        <Typography>{formData?.contactName}</Typography>
                                    </Grid>
                                </Grid>
                                <Grid container xs={12}>
                                    <Grid xs={6}>
                                        <Typography className="black">หมายเลขโทรศัพท์:</Typography>
                                    </Grid>
                                    <Grid xs={6}>
                                        <Typography>{formData?.contactPhone}</Typography>
                                    </Grid>
                                </Grid>
                                <Grid container xs={12}>
                                    <Grid xs={12} sm={6}>
                                        <Typography className="black">อีเมล:</Typography>
                                    </Grid>
                                    <Grid xs={12} sm={6}>
                                        <Typography>{formData?.contactEmail}</Typography>
                                    </Grid>
                                </Grid>
                                <Grid container xs={12}>
                                    <Grid xs={6}>
                                        <Typography className="black">Line ID:</Typography>
                                    </Grid>
                                    <Grid xs={6}>
                                        <Typography>{formData?.contactLineId}</Typography>
                                    </Grid>
                                </Grid>
                                <Grid container xs={12}>
                                    <Grid xs={6}>
                                        <Typography className="black">เลขทะเบียนพานิช:</Typography>
                                    </Grid>
                                    <Grid xs={6}>
                                        <Typography>{formData?.commRegisNumber}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: "center", px: 2, pb: 3 }}>
                        <Grid container spacing={1}>
                            <Grid xs={12} className="red" sx={{ textAlign: "center", pb: 2 }}>
                                หลังจากยืนยันแล้ว กรุณารอการตรวจสอบและอนุมัติจากเจ้าหน้าที่
                            </Grid>
                            <Grid xs={6} md={6}>
                                <Button fullWidth variant="outlined" onClick={() => setOpenDialog(false)} className="btn-cancle" > ยกเลิก </Button>
                            </Grid>
                            <Grid xs={6} md={6}>
                                <Button fullWidth variant="outlined" type="submit" onClick={handleConfirm} className="btn-success" sx={{ ml: "4px" }} > ยืนยัน </Button>
                            </Grid>
                        </Grid>
                    </DialogActions>
                </Box>
            </Dialog>
        </form>
    );
};

export default FormPlaceClaim;
